<template>
  <CContainer fluid>
    <CRow class="pt-3 mb-3">
      <CCol sm="12" class="d-flex justify-content-between align-items-center">
        <CButton color="secondary" @click="$router.push({ name: 'Dev Peoples' })">Back</CButton>
        <CButton color="info" @click="loadDetail">Refresh</CButton>
      </CCol>
    </CRow>

    <CRow>
      <CCol sm="12">
        <CCard>
          <CCardHeader>
            <div class="d-flex flex-wrap align-items-center">
              <h4 class="mb-0 mr-3">{{ header.title || 'User Data Debug Detail' }}</h4>
              <div class="text-muted mr-3">{{ header.display_name || summary.name || '-' }}</div>
              <div class="text-muted mr-3">{{ header.email || summary.email || '-' }}</div>
              <CBadge
                v-for="(badge, idx) in header.badges || []"
                :key="idx"
                class="mr-1"
                :color="badgeColor(badge.kind)"
              >{{ badge.label }}</CBadge>
            </div>
          </CCardHeader>
        </CCard>
      </CCol>
    </CRow>

    <CRow>
      <CCol sm="12">
        <CCard>
          <CCardHeader>Summary</CCardHeader>
          <CCardBody>
            <CRow>
              <CCol sm="3" v-for="(card, idx) in summaryCards" :key="idx" class="mb-2">
                <div class="p-2 border rounded bg-light">
                  <div class="text-muted small">{{ card.label }}</div>
                  <div class="font-weight-bold">{{ formatValue(card.value) }}</div>
                </div>
              </CCol>
            </CRow>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>

    <CRow>
      <CCol sm="12">
        <CCard>
          <CCardHeader class="d-flex justify-content-between align-items-center">
            <span>Relationship Health Check</span>
            <CButton size="sm" color="light" @click="healthOpen = !healthOpen">
              {{ healthOpen ? 'Hide' : 'Show' }}
            </CButton>
          </CCardHeader>
          <CCardBody v-show="healthOpen">
            <ul class="list-group">
              <li v-for="(h, idx) in healthChecks" :key="idx" class="list-group-item d-flex justify-content-between align-items-center">
                <span>{{ h.message }}</span>
                <span
                  :style="{ color: isHealthPass(h) ? '#2eb85c' : '#e55353', fontWeight: '700', fontSize: '18px' }"
                  :title="isHealthPass(h) ? 'PASS' : 'FAIL'"
                >
                  {{ isHealthPass(h) ? '✓' : '✗' }}
                </span>
              </li>
              <li v-if="!healthChecks.length" class="list-group-item">No checks available</li>
            </ul>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>

    <CRow>
      <CCol sm="12">
        <CCard>
          <CCardBody>
            <b-tabs content-class="mt-3" pills>
              <b-tab title="Overview" active>
                <data-table-block title="Overview Stats" :rows="overviewStats" />
              </b-tab>

              <b-tab title="User">
                <key-value-table title="User Info" :value="tabs.user.info" />
                <data-table-block title="Roles" :rows="toSimpleRows(tabs.user.roles, 'role')" />
                <data-table-block title="Permissions" :rows="toSimpleRows(tabs.user.permissions, 'permission')" />
                <data-table-block title="Device Tokens" :rows="normalizeList(tabs.devices.list)" />
              </b-tab>

              <b-tab title="People">
                <key-value-table title="People Info" :value="tabs.people.info" />
                <data-table-block title="Addresses" :rows="normalizeList(tabs.people.addresses)" />
                <data-table-block title="Relations" :rows="normalizeList(tabs.people.relations)" />
              </b-tab>

              <b-tab title="Member">
                <key-value-table title="Member Info" :value="tabs.member.info" />
                <data-table-block title="Notes" :rows="normalizeList(tabs.member.notes)" />
                <data-table-block title="Departments" :rows="normalizeList(tabs.member.departments)" />
                <data-table-block title="Class Attendance" :rows="normalizeList(tabs.member.class_attendance)" />
              </b-tab>

              <b-tab title="Visitor">
                <key-value-table title="Visitor Info" :value="tabs.visitor.info" />
                <data-table-block title="iCare Links" :rows="normalizeList(tabs.visitor.icare_links)" />
              </b-tab>

              <b-tab title="Newcomer">
                <key-value-table title="Newcomer Info" :value="tabs.newcomer.info" />
                <data-table-block title="Tujuan" :rows="normalizeList(tabs.newcomer.tujuan)" />
                <data-table-block title="Keperluan" :rows="normalizeList(tabs.newcomer.keperluan)" />
              </b-tab>

              <b-tab title="Devices">
                <table class="table table-sm table-bordered table-striped" v-if="normalizeList(tabs.devices.list).length">
                  <thead>
                    <tr>
                      <th>Platform</th>
                      <th>Device Name</th>
                      <th>Device Token</th>
                      <th>App Version</th>
                      <th>OS Version</th>
                      <th>Last Active</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="d in normalizeList(tabs.devices.list)" :key="d.device_token_id">
                      <td>
                        <CBadge v-if="d.is_ios" color="dark">iOS</CBadge>
                        <CBadge v-else-if="d.is_android" color="success">Android</CBadge>
                        <CBadge v-else color="secondary">{{ d.source || '-' }}</CBadge>
                      </td>
                      <td>{{ d.device_name || '-' }}</td>
                      <td>{{ d.device_token || '-' }}</td>
                      <td>{{ d.app_version || '-' }}</td>
                      <td>{{ d.os_version || '-' }}</td>
                      <td>{{ d.last_active_at || '-' }}</td>
                      <td>
                        <CButton size="sm" color="info" class="mr-1" @click="openTestModal(d)">Test Send</CButton>
                        <CButton size="sm" color="danger" @click="revokeDevice(d.device_token_id)">Revoke</CButton>
                      </td>
                    </tr>
                  </tbody>
                </table>
                <div v-else class="text-muted">No device registered.</div>
              </b-tab>

              <b-tab title="Orders / Events">
                <data-table-block title="Orders & Events" :rows="normalizeList(tabs.orders_events.list)" />
              </b-tab>

              <b-tab title="iCare">
                <key-value-table title="iCare Current (first record)" :value="firstRow(tabs.icare.icare.current_assignments)" />
                <data-table-block title="iCare Current Assignments" :rows="normalizeList(tabs.icare.icare.current_assignments)" />
                <data-table-block title="iCare Core Team" :rows="normalizeList(tabs.icare.icare.as_core_team)" />
                <data-table-block title="iCare Leader" :rows="normalizeList(tabs.icare.icare.as_leader)" />
                <data-table-block title="iCare Join Requests" :rows="normalizeList(tabs.icare.icare.join_requests)" />
                <data-table-block title="iCare Attendance Reports" :rows="normalizeList(tabs.icare.icare.attendance_reports)" />
                <data-table-block title="Class Attendance" :rows="normalizeList(tabs.icare.class_attendance)" />
              </b-tab>

              <b-tab title="iKids">
                <data-table-block title="Kids" :rows="normalizeList(tabs.ikids.kids)" />
                <data-table-block title="Queue History (current page payload)" :rows="normalizeList((tabs.ikids.queue_history || {}).rows)" />
              </b-tab>

              <b-tab title="Logs">
                <log-table title="people_logs" :rows="logData.people_logs.rows" :meta="logData.people_logs.meta" :page.sync="logPage.people_logs" @page-change="loadPeopleLogs" />
                <log-table title="peoples_activity" :rows="logData.people_activity.rows" :meta="logData.people_activity.meta" :page.sync="logPage.people_activity" @page-change="loadPeopleActivity" />
                <log-table title="peoples_status_movement" :rows="logData.people_status_movement.rows" :meta="logData.people_status_movement.meta" :page.sync="logPage.people_status_movement" @page-change="loadPeopleStatusMovement" />
              </b-tab>

              <b-tab title="Raw Data">
                <pre style="max-height:500px; overflow:auto">{{ prettyRaw }}</pre>
              </b-tab>
            </b-tabs>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>

    <CModal title="Test Send Notification" color="info" :show.sync="testModal">
      <div class="form-group">
        <label>Title</label>
        <input type="text" class="form-control" v-model="testForm.title" maxlength="100">
      </div>
      <div class="form-group">
        <label>Body</label>
        <textarea class="form-control" v-model="testForm.body" maxlength="255"></textarea>
      </div>
      <template #footer>
        <CButton color="secondary" @click="testModal = false">Cancel</CButton>
        <CButton color="info" :disabled="testSending" @click="sendTest">Send</CButton>
      </template>
    </CModal>
  </CContainer>
</template>

<script>
import devService from '@/services/dev'
import notificationLogs from '@/services/notificationLogs'

const KeyValueTable = {
  props: { title: String, value: { type: Object, default: () => ({}) } },
  methods: {
    format(v) {
      if (v == null || v === '') return '-'
      if (typeof v === 'boolean') return v ? 'Yes' : 'No'
      return String(v)
    }
  },
  template: `
    <div class="mb-3">
      <h6>{{ title }}</h6>
      <table class="table table-sm table-bordered">
        <tbody>
          <tr v-for="(v, k) in value" :key="k">
            <th style="width:35%">{{ k }}</th>
            <td>{{ format(v) }}</td>
          </tr>
          <tr v-if="!value || Object.keys(value).length===0"><td colspan="2" class="text-center">No data</td></tr>
        </tbody>
      </table>
    </div>
  `
}

const DataTableBlock = {
  props: { title: String, rows: { type: Array, default: () => [] } },
  data() { return { page: 1, perPage: 10 } },
  computed: {
    cols() {
      const first = this.rows[0]
      if (!first || typeof first !== 'object' || Array.isArray(first)) return []
      return Object.keys(first)
    },
    pagedRows() {
      const start = (this.page - 1) * this.perPage
      return this.rows.slice(start, start + this.perPage)
    }
  },
  methods: {
    format(v) {
      if (v == null || v === '') return '-'
      if (typeof v === 'boolean') return v ? 'Yes' : 'No'
      if (typeof v === 'object') return JSON.stringify(v)
      return String(v)
    }
  },
  template: `
    <div class="mb-4">
      <h6>{{ title }}</h6>
      <table class="table table-sm table-bordered table-striped" v-if="rows.length">
        <thead>
          <tr>
            <th v-for="col in cols" :key="col">{{ col }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, ridx) in pagedRows" :key="ridx">
            <td v-for="col in cols" :key="col">{{ format(row[col]) }}</td>
          </tr>
        </tbody>
      </table>
      <div v-else class="text-muted">No data</div>
      <b-pagination v-if="rows.length > perPage" v-model="page" :total-rows="rows.length" :per-page="perPage"></b-pagination>
    </div>
  `
}

const LogTable = {
  components: { DataTableBlock },
  props: {
    title: String,
    rows: { type: Array, default: () => [] },
    meta: { type: Object, default: () => ({}) },
    page: { type: Number, default: 1 }
  },
  methods: {
    changePage(v) {
      this.$emit('update:page', v)
      this.$emit('page-change', v)
    }
  },
  template: `
    <div class="mb-4">
      <data-table-block :title="title" :rows="rows" />
      <b-pagination
        v-if="(meta.total || 0) > (meta.per_page || 10)"
        :value="page"
        :total-rows="meta.total || 0"
        :per-page="meta.per_page || 10"
        @change="changePage"
      ></b-pagination>
    </div>
  `
}

export default {
  components: { KeyValueTable, DataTableBlock, LogTable },
  data() {
    return {
      summary: {},
      header: { title: '', display_name: '', email: '', badges: [] },
      summaryCards: [],
      healthChecks: [],
      healthOpen: true,
      tabs: {
        overview: { stats: [] },
        user: { info: {}, roles: [], permissions: [] },
        people: { info: {}, addresses: [], relations: [] },
        member: { info: {}, notes: [], departments: [], class_attendance: [] },
        visitor: { info: {}, icare_links: [] },
        newcomer: { info: {}, tujuan: [], keperluan: [] },
        ikids: { kids: [], queue_history: { rows: [], meta: {} } },
        devices: { list: [] },
        orders_events: { list: [] },
        icare: { icare: {}, class_attendance: [] },
        logs: {},
        raw_data: {}
      },
      data: {},
      logPage: {
        people_logs: 1,
        people_activity: 1,
        people_status_movement: 1
      },
      logData: {
        people_logs: { rows: [], meta: {} },
        people_activity: { rows: [], meta: {} },
        people_status_movement: { rows: [], meta: {} }
      },
      testModal: false,
      testSending: false,
      testDeviceId: null,
      testForm: {
        title: '',
        body: ''
      }
    }
  },
  computed: {
    overviewStats() {
      return this.normalizeList(this.tabs.overview.stats)
    },
    prettyRaw() {
      try {
        return JSON.stringify(this.tabs.raw_data || this.data || {}, null, 2)
      } catch (e) {
        return '{}'
      }
    }
  },
  mounted() {
    this.loadDetail()
  },
  methods: {
    badgeColor(kind) {
      if (kind === 'active' || kind === 'success') return 'success'
      if (kind === 'inactive' || kind === 'error') return 'danger'
      if (kind === 'warning') return 'warning'
      return 'info'
    },
    isHealthPass(item) {
      return item && (item.ok === true || item.level === 'success')
    },
    formatValue(v) {
      if (v == null || v === '') return '-'
      if (typeof v === 'boolean') return v ? 'Yes' : 'No'
      return String(v)
    },
    normalizeList(value) {
      if (Array.isArray(value)) return value
      if (value && typeof value === 'object' && Array.isArray(value.items)) return value.items
      return []
    },
    toSimpleRows(arr, keyName) {
      const list = this.normalizeList(arr)
      return list.map((x) => ({ [keyName]: x }))
    },
    firstRow(arr) {
      const list = this.normalizeList(arr)
      return list.length ? list[0] : {}
    },
    normalizeLogPayload(raw) {
      const payload = raw || {}
      let rows = []
      if (Array.isArray(payload.rows)) rows = payload.rows
      else if (payload.rows && typeof payload.rows === 'object' && Array.isArray(payload.rows.items)) rows = payload.rows.items
      else if (Array.isArray(payload)) rows = payload
      return { rows, meta: payload.meta || {} }
    },
    loadDetail() {
      devService.getDevPeopleDetail(this.$route.params.people_uuid)
        .then((res) => {
          const payload = res.data || {}
          this.data = payload
          this.summary = payload.summary || {}
          this.header = payload.header || { title: 'User Data Debug Detail', badges: [] }
          this.summaryCards = this.normalizeList(payload.summary_cards)
          this.healthChecks = this.normalizeList(payload.health_checks)
          this.tabs = payload.tabs || this.tabs

          this.logData.people_logs = this.normalizeLogPayload((payload.logs || {}).people_logs)
          this.logData.people_activity = this.normalizeLogPayload((payload.logs || {}).people_activity)
          this.logData.people_status_movement = this.normalizeLogPayload((payload.logs || {}).people_status_movement)
        })
        .catch((err) => {
          alert((err.response && err.response.data && err.response.data.message) || 'Failed to load detail')
          this.$router.push({ name: 'Dev Peoples' })
        })
    },
    loadPeopleLogs() {
      devService.getDevPeopleLogs(this.$route.params.people_uuid, { page: this.logPage.people_logs, per_page: 10 })
        .then((res) => {
          this.logData.people_logs = this.normalizeLogPayload(res.data)
        })
    },
    loadPeopleActivity() {
      devService.getDevPeopleActivity(this.$route.params.people_uuid, { page: this.logPage.people_activity, per_page: 10 })
        .then((res) => {
          this.logData.people_activity = this.normalizeLogPayload(res.data)
        })
    },
    loadPeopleStatusMovement() {
      devService.getDevPeopleStatusMovement(this.$route.params.people_uuid, { page: this.logPage.people_status_movement, per_page: 10 })
        .then((res) => {
          this.logData.people_status_movement = this.normalizeLogPayload(res.data)
        })
    },
    revokeDevice(tokenId) {
      if (!confirm('Do you really want to revoke this device?')) {
        return
      }
      notificationLogs.revokeDeviceToken(tokenId)
        .then(() => {
          this.loadDetail()
        })
        .catch((err) => {
          alert((err.response && err.response.data && err.response.data.message) || 'Failed to revoke device')
        })
    },
    openTestModal(d) {
      this.testDeviceId = d.device_token_id
      this.testForm.title = ''
      this.testForm.body = ''
      this.testModal = true
    },
    sendTest() {
      if (!this.testForm.title || !this.testForm.body) {
        alert('Title and body are required')
        return
      }
      this.testSending = true
      notificationLogs.sendTestToDevice(this.testDeviceId, this.testForm)
        .then((res) => {
          this.testSending = false
          this.testModal = false
          const success = res.data && res.data.success
          alert(success ? 'Test notification sent' : ((res.data && res.data.error) || 'Send failed'))
        })
        .catch((err) => {
          this.testSending = false
          alert((err.response && err.response.data && err.response.data.message) || 'Failed to send test notification')
        })
    }
  }
}
</script>
